// Phase 4 Task 9 — PieceSprite: imperative Pixi Container wrapping a piece
// token drawn by `drawPiece` (PieceLayer.tsx).
//
// Design (Approach A — mirror LetterCubeSprite/LetterCubeSpriteMount):
//   - Pixi `Container` subclass owning a Graphics child.
//   - `updatePiece(props)` re-draws the Graphics when piece data changes.
//   - `updatePosition(x, y, reducedMotion)` glides via createPositionAnchorTween
//     (full motion, position actually changed) or snaps (RM / first set).
//   - `_activeReanchorTween` is drained in `destroy()` before super.destroy()
//     (Pitfall 5 lock).
//
// GLIDE-FROM-NOW (simpler than the cube's $boardReanchor FSM):
//   The chess view is mutually exclusive with the letter view and does NOT
//   show the flop/river reveal cinematic. There is no $boardReanchor signal
//   to gate on. When a piece's logical position changes (field grew → rows
//   recalculated → cellIdxToScreen differs), we glide from the current
//   (old) pixel position to the new one immediately. Reduced motion snaps.
//   First mount is always a snap (positions already equal at mount time).
//
// drawPiece + 3-channel identity (shape/position/color) are UNCHANGED. This
// file only adds the imperative container wrapper + glide update surface.

import { Container, Graphics } from 'pixi.js'
import type { Group, Tween } from 'tweedle.js'
import type { G, Kind } from '../layers/PieceLayer'
import { drawPiece } from '../layers/PieceLayer'
import { createPositionAnchorTween } from '../motion/position-anchor-tween'
import { selectDuration } from '../motion/tween-config'

export interface PieceSpriteProps {
  kind: Kind
  ownerSlot: 0 | 1
  fill: number
  glow: number
  magic: number
  empowered: boolean
  tweenGroup: Group
}

export class PieceSprite extends Container {
  private readonly _gfx: Graphics
  private readonly _tweenGroup: Group

  // Last-drawn state for diff-gating the redraw.
  private _kind: Kind
  private _ownerSlot: 0 | 1
  private _fill: number
  private _glow: number
  private _magic: number
  private _empowered: boolean

  /** In-flight position tween — drained in destroy() (Pitfall 5 lock). */
  private _activeReanchorTween: Tween<{ x: number; y: number }> | null = null

  /** True after the first call to updatePosition — guards first-mount snap. */
  private _hasInitialPosition = false

  constructor(props: PieceSpriteProps) {
    super()
    this._tweenGroup = props.tweenGroup
    this._kind = props.kind
    this._ownerSlot = props.ownerSlot
    this._fill = props.fill
    this._glow = props.glow
    this._magic = props.magic
    this._empowered = props.empowered

    this._gfx = new Graphics()
    this.addChild(this._gfx)
    this._draw()
  }

  /** Re-draw only when at least one visual property changed. */
  updatePiece(props: Omit<PieceSpriteProps, 'tweenGroup'>): void {
    const changed =
      props.kind !== this._kind ||
      props.ownerSlot !== this._ownerSlot ||
      props.fill !== this._fill ||
      props.glow !== this._glow ||
      props.magic !== this._magic ||
      props.empowered !== this._empowered

    if (!changed) return

    this._kind = props.kind
    this._ownerSlot = props.ownerSlot
    this._fill = props.fill
    this._glow = props.glow
    this._magic = props.magic
    this._empowered = props.empowered
    this._draw()
  }

  /**
   * Apply a position update. On first call (mount) always snaps. On
   * subsequent calls: full motion + moved → glide from current to new;
   * reduced motion or not moved → snap.
   */
  updatePosition(x: number, y: number, reducedMotion: boolean): void {
    if (!this._hasInitialPosition) {
      // First mount: snap directly (no glide on initial placement).
      this.x = x
      this.y = y
      this._hasInitialPosition = true
      return
    }

    const moved = this.x !== x || this.y !== y
    if (!moved) {
      // Already at target: idempotent snap (covers no-move path).
      return
    }

    if (reducedMotion) {
      this.x = x
      this.y = y
      return
    }

    // Full motion + moved: cancel any in-flight tween, then glide from now.
    if (this._activeReanchorTween !== null) {
      this._tweenGroup.remove(this._activeReanchorTween)
      this._activeReanchorTween = null
    }

    const duration = selectDuration('dur-board-grow', reducedMotion)
    const tweenRef = createPositionAnchorTween({
      target: this,
      fromPos: { x: this.x, y: this.y },
      toPos: { x, y },
      duration,
      group: this._tweenGroup,
      onComplete: () => {
        if (this._activeReanchorTween === tweenRef) {
          this._activeReanchorTween = null
        }
      },
    })
    this._activeReanchorTween = tweenRef
  }

  private _draw(): void {
    drawPiece(
      this._gfx as unknown as G,
      this._kind,
      this._ownerSlot,
      this._fill,
      this._glow,
      this._magic,
      this._empowered,
    )
  }

  /**
   * Destroy this sprite. Drains the in-flight re-anchor tween from the
   * shared Group BEFORE super.destroy() so the Group's next update() pass
   * cannot mutate a destroyed Container's x/y (Pitfall 5 lock).
   */
  override destroy(options?: { children?: boolean }): void {
    if (this._activeReanchorTween !== null) {
      this._tweenGroup.remove(this._activeReanchorTween)
      this._activeReanchorTween = null
    }
    super.destroy({ children: options?.children ?? true })
  }
}
